// ============================================================================
// PORTAL DO AFILIADO — PREMIUM V7.2
// UPDATED: 2026-03-27
// ✅ FeedNovidades integrado — aba "Novidades" + seção no dashboard
// ============================================================================
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Home, Users, UserPlus, BookOpen, FileText,
  RefreshCw, LogOut, Menu, X, Bell, Settings, ChevronRight,
  Megaphone,
} from 'lucide-react';
import { affiliateService } from '../services/affiliateService';
import { authService } from '../services/authService';
import { updateUser } from '../store/slices/authSlice';

import DashboardAfiliado    from '../components/PortalAfiliado/DashboardAfiliado';
import ListaAlunos          from '../components/PortalAfiliado/ListaAlunos';
import CadastroAlunoForm    from '../components/PortalAfiliado/CadastroAlunoForm';
import DocumentosAfiliado   from '../components/PortalAfiliado/DocumentosAfiliado';
import GerenciamentoCursos  from '../components/PortalAfiliado/GerenciamentoCursos';
import RenovacaoAnual       from '../components/PortalAfiliado/RenovacaoAnual';
import FeedNovidades        from '../components/shared/FeedNovidades';

interface RootState {
  auth: {
    user: {
      name: string;
      email: string;
      schoolName?: string;
      affiliateId?: string;
      logoUrl?: string; // ✅ NOVO — foto de perfil cadastrada
    } | null;
  };
}
type TabType = 'dashboard' | 'alunos' | 'cadastrar' | 'documentos' | 'cursos' | 'renovacao' | 'novidades';

const SB_BG     = '#2b2b2e';
const W_SIDEBAR = 'lg:w-72';
const PortalAfiliado: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeTab,   setActiveTab]   = useState<TabType>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ✅ Busca os dados atuais do afiliado assim que o portal carrega —
  // evita exibir nome/foto/escola desatualizados (retrato salvo no login)
  useEffect(() => {
    affiliateService.getMyAffiliate()
      .then(fresh => {
        dispatch(updateUser({
          name:       fresh.responsibleName || fresh.schoolName,
          schoolName: fresh.schoolName,
          logoUrl:    fresh.logoUrl,
        }));
      })
      .catch(() => {
        // se falhar, mantém o que já está no Redux
      });
  }, [dispatch]);

  const menuItems = [
    { id: 'dashboard'  as TabType, label: 'Início',          icon: Home      },
    { id: 'novidades'  as TabType, label: 'Novidades',       icon: Megaphone },
    { id: 'alunos'     as TabType, label: 'Meus Alunos',     icon: Users     },
    { id: 'cadastrar'  as TabType, label: 'Cadastrar Aluno', icon: UserPlus  },
    { id: 'cursos'     as TabType, label: 'Meus Cursos',     icon: BookOpen  },
    { id: 'documentos' as TabType, label: 'Base Didática',      icon: FileText  },
    { id: 'renovacao'  as TabType, label: 'Renovação Anual', icon: RefreshCw },
  ];

  const handleLogout = async () => {
    await authService.logout();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardAfiliado
            onCadastrarClick={() => setActiveTab('cadastrar')}
            onCursosClick={() => setActiveTab('cursos')}
            onDocumentosClick={() => setActiveTab('documentos')}
            onRenovacaoClick={() => setActiveTab('renovacao')}
          />
        );
      case 'novidades':  return <NovidadesPage />;
      case 'alunos':
        return <ListaAlunos onCadastrarClick={() => setActiveTab('cadastrar')} />;
      case 'cadastrar':  return <CadastroAlunoForm />;
      case 'documentos': return <DocumentosAfiliado />;
      case 'cursos':     return <GerenciamentoCursos />;
      case 'renovacao':  return <RenovacaoAnual />;
      default:           return <DashboardAfiliado />;
    }
  };

  const initials = user?.name
    ?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || 'FA';

  // ── Nav ──────────────────────────────────────────────────────────────────
  const SidebarNav = ({ onSelect }: { onSelect?: () => void }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
      {menuItems.map((item) => {
        const Icon   = item.icon;
        const active = activeTab === item.id;
        return (
          <button
            key={item.id}
            onClick={() => { setActiveTab(item.id); onSelect?.(); }}
            className={`
              relative w-full group flex items-center gap-3.5 px-4 py-2.5 rounded-xl
              text-sm font-medium transition-all duration-200 overflow-hidden
              ${active
                ? 'text-[#1a1a1c]'
                : 'text-[#a0a0a8] hover:text-gold-300 hover:bg-white/[0.05]'}
            `}
          >
            {active && (
              <>
                <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600" />
                <span className="absolute inset-0 rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_4px_20px_rgba(212,175,55,0.40)]" />
              </>
            )}
            {!active && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-0
                group-hover:h-7 rounded-r-full bg-gold-500/50 transition-all duration-200" />
            )}
            <span className={`
              relative z-10 flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0
              transition-all duration-200
              ${active
                ? 'bg-black/[0.12] text-[#1a1a1c]'
                : 'bg-white/[0.06] group-hover:bg-gold-500/12 group-hover:text-gold-400'}
            `}>
              <Icon className="w-[17px] h-[17px]" />
            </span>
            <span className="relative z-10 flex-1 text-left tracking-wide">{item.label}</span>
            {active && (
              <ChevronRight className="relative z-10 w-3.5 h-3.5 opacity-35 flex-shrink-0" />
            )}
          </button>
        );
      })}
    </nav>
  );

  // ── Sidebar inner ────────────────────────────────────────────────────────
  const SidebarInner = ({ onSelect, showClose }: { onSelect?: () => void; showClose?: boolean }) => (
    <>
      <div className="relative px-6 py-5 border-b border-white/[0.07]">
        <div className="absolute top-0 left-6 right-6 h-[1px]
          bg-gradient-to-r from-transparent via-gold-500/55 to-transparent" />
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-gold-400/20 blur-[10px]" />
            <img src="/imagem/Bras.png" alt="FEPCSSP"
              className="relative w-11 h-11 object-contain drop-shadow-[0_2px_8px_rgba(212,175,55,0.45)]" />
          </div>
          <div className="flex-1">
            <p className="text-[15px] font-bold tracking-[0.18em] text-gold-400 leading-none">FEPCSSP</p>
            <p className="text-[9.5px] text-[#7a7a82] uppercase tracking-[0.22em] mt-1 font-medium">Portal do Filiado</p>
          </div>
          <span className="text-[8.5px] font-bold tracking-widest text-gold-500
            bg-gold-500/10 border border-gold-500/22 px-2 py-0.5 rounded-md uppercase leading-none">
            PRO
          </span>
          {showClose && (
            <button onClick={() => setSidebarOpen(false)}
              className="ml-1 p-1.5 hover:bg-white/10 rounded-lg text-[#7a7a82] transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* User card */}
      <div className="px-4 py-3 mx-3 mt-4 mb-2 rounded-2xl bg-white/[0.04] border border-gold-500/[0.13]">
        <div className="flex items-center gap-3">
      <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-gold-500/25 blur-[8px]" />
            {user?.logoUrl ? (
              <img
                src={user.logoUrl}
                alt={user?.name || 'Filiado'}
                className="relative w-10 h-10 rounded-full object-cover ring-2 ring-gold-500/28
                  shadow-[0_0_12px_rgba(212,175,55,0.30)]"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="relative w-10 h-10 rounded-full flex items-center justify-center
                text-[#1a1a1c] font-semibold text-sm ring-2 ring-gold-500/28
                shadow-[0_0_12px_rgba(212,175,55,0.30)]"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#b8860b)' }}>
                {initials}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full
              shadow-[0_0_6px_rgba(37,211,102,0.55)]"
              style={{ border: `2px solid ${SB_BG}` }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-white/85 truncate leading-tight">
              {user?.name || 'Filiado'}
            </p>
            <p className="text-[10.5px] text-[#7a7a82] truncate mt-0.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-gold-500/65 rounded-full flex-shrink-0" />
              {user?.schoolName || 'Escola Associada'}
            </p>
          </div>
        </div>
      </div>

      <div className="px-7 mb-2 mt-1">
        <p className="text-[9px] font-semibold tracking-[0.22em] text-[#5a5a62] uppercase">Navegação</p>
      </div>

      <SidebarNav onSelect={onSelect} />

      <div className="px-3 pb-5 pt-2">
        <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent mb-3" />
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl
            text-sm font-medium text-[#7a7a82] hover:bg-red-500/10 hover:text-red-400
            transition-all duration-150 group">
          <span className="w-8 h-8 rounded-lg bg-white/[0.05] group-hover:bg-red-500/15
            flex items-center justify-center transition-colors flex-shrink-0">
            <LogOut className="w-4 h-4" />
          </span>
          Sair da conta
        </button>
      </div>
    </>
  );

  const sidebarShell = 'flex flex-col relative border-r border-white/[0.06]';

  const GoldEdge = () => (
    <div className="absolute right-0 top-0 bottom-0 w-[1px]
      bg-gradient-to-b from-transparent via-gold-600/22 to-transparent pointer-events-none" />
  );

  const TextureOverlay = () => (
    <div className="absolute inset-0 pointer-events-none" style={{
      background: `
        radial-gradient(ellipse 80% 40% at 50% 0%,   rgba(212,175,55,0.06) 0%, transparent 70%),
        radial-gradient(ellipse 60% 30% at 50% 100%,  rgba(0,0,0,0.25) 0%, transparent 70%)
      `
    }} />
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* SIDEBAR DESKTOP */}
      <aside className={`hidden lg:flex ${W_SIDEBAR} flex-shrink-0 ${sidebarShell}`}
        style={{ backgroundColor: SB_BG }}>
        <TextureOverlay />
        <GoldEdge />
        <SidebarInner />
      </aside>

      {/* SIDEBAR MOBILE */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/75 backdrop-blur-[3px] z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
          <aside className={`fixed inset-y-0 left-0 w-72 z-50 lg:hidden
            shadow-[4px_0_40px_rgba(0,0,0,0.5)] ${sidebarShell}`}
            style={{ backgroundColor: SB_BG }}>
            <TextureOverlay />
            <GoldEdge />
            <SidebarInner onSelect={() => setSidebarOpen(false)} showClose />
          </aside>
        </>
      )}

      {/* MAIN */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Mobile header */}
        <header className="relative lg:hidden flex items-center justify-between
          px-4 py-3.5 border-b border-white/[0.07]
          shadow-[0_4px_20px_rgba(0,0,0,0.35)] flex-shrink-0"
          style={{ backgroundColor: SB_BG }}>
          <div className="absolute bottom-0 left-0 right-0 h-[1px]
            bg-gradient-to-r from-transparent via-gold-600/28 to-transparent" />
          <button onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-white/10 rounded-xl text-gold-400 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 bg-gold-400/20 blur-md rounded-full" />
              <img src="/imagem/Bras.png" alt="" className="relative w-7 h-7 object-contain" />
            </div>
            <span className="font-bold text-gold-400 tracking-widest text-sm">FEPCSSP</span>
          </div>
          <button className="relative p-2 hover:bg-white/10 rounded-xl text-[#7a7a82] transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full
              shadow-[0_0_6px_rgba(239,68,68,0.65)]"
              style={{ border: `2px solid ${SB_BG}` }} />
          </button>
        </header>

        {/* Desktop header */}
        <header className="relative hidden lg:flex items-center justify-between
          px-8 py-4 bg-white border-b border-gray-100 flex-shrink-0
          shadow-[0_1px_12px_rgba(0,0,0,0.06)]">
          <div className="absolute top-0 left-0 right-0 h-[2px]
            bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              {menuItems.find(i => i.id === activeTab)?.label}
            </h2>
            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5 tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500 inline-block" />
              {user?.schoolName || 'Escola Associada'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button className="relative p-2.5 hover:bg-gray-50 rounded-xl transition-colors group">
              <Bell className="w-5 h-5 text-gray-400 group-hover:text-gray-700 transition-colors" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full
                ring-2 ring-white shadow-[0_0_6px_rgba(239,68,68,0.55)]" />
            </button>
            <button className="p-2.5 hover:bg-gray-50 rounded-xl transition-colors group">
              <Settings className="w-5 h-5 text-gray-400 group-hover:text-gray-700 transition-colors" />
            </button>
            <div className="w-px h-6 bg-gray-200 mx-2" />
            <div className="flex items-center gap-2.5 pl-0.5 pr-3 py-1.5 rounded-xl
              bg-gray-50 border border-gray-100 hover:border-gold-200 transition-colors cursor-default">
           <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gold-400/20 blur-[6px]" />
                {user?.logoUrl ? (
                  <img
                    src={user.logoUrl}
                    alt={user?.name || 'Filiado'}
                    className="relative w-8 h-8 rounded-full object-cover ring-2 ring-gold-200 shadow-gold"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="relative w-8 h-8 rounded-full flex items-center justify-center
                    text-[#1a1a1c] font-semibold text-xs ring-2 ring-gold-200 shadow-gold"
                    style={{ background: 'linear-gradient(135deg,#f4d03f,#b8860b)' }}>
                    {initials}
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500
                  rounded-full border-2 border-white" />
              </div>
              <div className="hidden xl:block leading-tight">
                <p className="text-[13px] font-medium text-gray-800">
                  {user?.name?.split(' ')[0] || 'Filiado'}
                </p>
                <p className="text-[10px] text-gray-400">Filiado Ativo</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 px-4 py-6 md:px-8 md:py-8">
          {renderContent()}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-100 px-6 py-3 text-center flex-shrink-0">
          <p className="text-[11px] text-gray-400 tracking-wide">
            Federação dos Profissionais da Beleza
            <span className="mx-2 text-gray-200">·</span>
            <span className="text-gold-600 font-medium">Área restrita do Filiado</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

// ── Página de Novidades (aba dedicada) ────────────────────────────────────────
const NovidadesPage: React.FC = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-3.5">
      <div className="relative flex-shrink-0">
        <div className="absolute -inset-1 bg-amber-400/20 blur-[10px] rounded-full" />
        <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
          <Megaphone className="w-5 h-5 text-[#1a1a1c]" />
        </div>
      </div>
      <div>
        <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
          Novidades
        </h1>
        <p className="text-xs text-gray-400 mt-1">
          Atualizações, serviços e informações da Federação
        </p>
      </div>
    </div>
    <FeedNovidades maxItems={12} />
  </div>
);

export default PortalAfiliado;
